import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FieldExpression, NodeExpression, toString as luceneToString } from 'lucene';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';
import { Subscription } from 'rxjs';

import { AdminService } from '../../admin.service';
import { PendUser, Users } from '../../models/administration';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { UserService } from '../../../shared/services/user.service';

@Component({
	selector: 'copo-administration',
	templateUrl: './administration.component.html',
	styleUrl: './administration.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		NgFor,

		MatButtonModule,
		MatIconModule,
	],
})
export class AdministrationComponent implements OnInit, OnDestroy {
	pendUserData: PendUser[] = [];
	fields = [
		'id',
		'status',
		'firstName',
		'lastName',
		'personMembers.status',
		'personMembers.member.id',
		'personMembers.member.name',
		'personMembers.role',
	];

	rejSub: Subscription | undefined;
	pendSub: Subscription | undefined;

	constructor(private readonly adminService: AdminService, private readonly userService: UserService) {}

	ngOnInit(): void {
		this.getPendUsersData();
	}

	ngOnDestroy(): void {
		this.rejSub?.unsubscribe();
		this.pendSub?.unsubscribe();
	}

	async updateUserRole(userId: number, orgId: number) {
		try {
			await this.adminService.updateMemberRole(userId, orgId).then(() => {
				this.pendUserData = [];
				this.getPendUsersData();
			});
		} catch (err) {
			console.error('Error from http put current user detail', err);
		}
	}

	rejectUser(userId: number, orgId: number) {
		this.rejSub = this.adminService.rejectUserRole(userId, orgId).subscribe({
			next: () => {
				this.pendUserData = [];
				this.getPendUsersData();
			},
			error: (err: unknown) => {
				console.error('error requesting user', err);
			},
		});
	}

	private getOrganizationId() {
		const loginUser = this.userService.loggedUser;
		if (loginUser === undefined) {
			return undefined;
		}
		return loginUser.currentVisibility.organizationId;
	}

	private buildFilter() {
		const orgId = this.getOrganizationId();
		const memberStatus: FieldExpression = {
			field: 'personMembers.status',
			term: 'pend',
			quoted: true,
		};
		const memberOrgId: FieldExpression = {
			field: 'personMembers.member.id',

			term: `${ orgId }`,
			quoted: true,
		};
		const userPendStatus: FieldExpression = {
			field: 'status',
			term: 'pend',
			quoted: true,
		};
		const userActivStatus: FieldExpression = {
			field: 'status',
			term: 'activ',
			quoted: true,
		};
		const userNewStatus: FieldExpression = {
			field: 'status',
			term: 'NEW',
			quoted: true,
		};
		const statusNode: NodeExpression = {
			left: userPendStatus,
			operator: 'OR',
			right: userActivStatus,
			parenthesized: false,
		};
		const statusOrgNode: NodeExpression = {
			left: memberStatus,
			operator: 'AND',
			right: memberOrgId,
			parenthesized: false,
		};
		const userStatusFilter: NodeExpression = {
			left: statusNode,
			operator: 'OR',
			right: userNewStatus,
			parenthesized: true,
		};
		const finalFilter: NodeExpression = {
			left: statusOrgNode,
			operator: 'AND',
			right: userStatusFilter,
			parenthesized: false,
		};
		return luceneToString(finalFilter);
	}

	private getPendUsersData() {
		this.pendSub = this.adminService.getPendUsers(0, 100, false, <(keyof Users)[]> this.fields, this.buildFilter())
			.subscribe({
				next: res => {
					if (res.results.length > 0) {
						for (const data of res.results) {
							for (const personData of data.personMembers) {
								if (personData.status === 'pend') {
									const pendResultObj = {
										fullName: `${ data.firstName } ${ data.lastName }`,
										orgName: personData.member.name,
										role: personData.role,
										orgId: personData.member.id,
										userId: data.id,
										personMemberId: personData.id
									};
									this.pendUserData.push(pendResultObj);
								}
							}
						}
					}
				},
				error: (err: unknown) => {
					console.error('error requesting user data', err);
				},
			});
	}
}
